import React, { useMemo } from 'react';
import type { PieTooltipProps } from '@nivo/pie';
import { ComputedDatum, Pie as NevoPie, ResponsivePie } from '@nivo/pie';

import type { PieDatum, PieFormat, PieProps } from './Pie.types';

function getFormattedValue(value: number, total: number, format: PieFormat): string {
  if (format === 'dollars') {
    return `$${value.toLocaleString()}`;
  }
  return `${((value / total) * 100).toFixed(0)}%`;
}

const makeTooltip = (total: number, tooltipFormat: PieFormat) => {
  const TooltipComponent = ({ datum }: PieTooltipProps<PieDatum>) => (
    <div className="pie-tooltip" style={{ color: datum.color || '#333' }}>
      <span style={{ fontWeight: 600 }}>{datum.label}</span>:{' '}
      {getFormattedValue(datum.value as number, total, tooltipFormat)}
    </div>
  );
  TooltipComponent.displayName = 'PieTooltip';
  return TooltipComponent;
};

const defaultMargin = { top: 20, right: 20, bottom: 20, left: 20 };

/**
 * Pie Component
 *
 * Renders a pure pie chart using Nivo. No legend or title included.
 *
 * @component
 * @param {PieProps} props - Props for the pie chart, including data, formatting, and display options.
 * @returns {JSX.Element | null}
 */

export const Pie: React.FC<PieProps> = (props) => {
  const {
    data,
    margin = defaultMargin,
    colors = { datum: 'data.color' },
    arcLabelsTextColor = { from: 'color', modifiers: [['darker', 2]] },
    arcLinkLabelsOffset = -20,
    arcLinkLabelsTextColor = { from: 'color', modifiers: [] },
    arcLinkLabelsThickness = 0,
    arcLinkLabelsColor = { from: 'color', modifiers: [] },
    cornerRadius = 1,
    activeOuterRadiusOffset = 8,
    arcLabelFormat = 'percentage',
    tooltipFormat = 'percentage',
    showArcLabels = false,
    arcLinkLabelType = 'value',
    ...rest
  } = props;

  const total = useMemo(() => data.reduce((sum, item) => sum + item.value, 0), [data]);
  const arcLabel = useMemo(
    () => (datum: { value: number }) => getFormattedValue(datum.value, total, arcLabelFormat),
    [total, arcLabelFormat],
  );
  const arcLinkLabel = useMemo(
    () =>
      arcLinkLabelType === 'value'
        ? (datum: ComputedDatum<PieDatum>) => String(getFormattedValue(datum.value, total, arcLabelFormat))
        : (datum: ComputedDatum<PieDatum>) => String(datum.label),
    [arcLinkLabelType, total, arcLabelFormat],
  );
  const tooltip = useMemo(() => makeTooltip(total, tooltipFormat), [total, tooltipFormat]);

  const commonProps = {
    data,
    margin,
    colors,
    arcLabel,
    arcLabelsTextColor,
    arcLinkLabelsOffset,
    arcLinkLabelsTextColor,
    arcLinkLabelsThickness,
    arcLinkLabelsColor,
    arcLinkLabel,
    enableArcLabels: showArcLabels,
    cornerRadius,
    activeOuterRadiusOffset,
    tooltip,
    ...rest,
  };

  if (props.responsive === undefined || props.responsive === true) {
    return <ResponsivePie {...commonProps} />;
  } else if (props.responsive === false) {
    return <NevoPie {...commonProps} height={props.height} width={props.width} />;
  }
  return null;
};
